import React from 'react';
import { SORT_DIRECTIONS, SortConfig } from './constants/commonConstants';
import SortIcon from '../modules/Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import sortIconStyles from '../modules/Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon.module.scss';
import Tooltip from '../common/_custom/Tooltip/Tooltip';
import { infoIconGray } from '../assets/hostedassets';

/** none -> ASC -> DESC -> none on the same column; a different column starts at ASC. */
export function nextSortConfig(current: SortConfig | undefined, field: string): SortConfig | undefined {
    if (current?.field !== field) return { field, direction: SORT_DIRECTIONS.ASC };
    if (current.direction === SORT_DIRECTIONS.ASC) return { field, direction: SORT_DIRECTIONS.DESC };
    return undefined;
}

/**
 * Configuration options for processing sortable headers
 */
interface ProcessSortableHeadersOptions {
    /** Headers configuration object containing columns array */
    headers: any;
    /** Current sort configuration with field and direction */
    sortConfig?: SortConfig;
    /** Callback function to handle sorting when a column header is clicked */
    onSort?: (field: string, fieldType?: string) => void;
    /** Selected category/view type (e.g., 'MANDATE_VIEW', 'DEAL_VIEW') */
    selectedCategory?: string;
    /** Deal view types constants for determining layout adjustments */
    dealViewTypes?: Record<string, string>;
}

/**
 * Processes column headers to add sort icons for sortable columns.
 * This utility consolidates the common logic for adding interactive sort functionality
 * to data table headers across different components.
 *
 * Features:
 * - Adds sort icons to columns with sortConfig.enabled = true
 * - Handles hover states for inactive sort icons
 * - Maintains active sort icon visibility
 * - Applies special spacing for last column in Mandate View
 * - Supports both sortConfig.field and column.field for flexibility
 *
 * @param options Configuration options for processing headers
 * @returns Processed headers object with enhanced column headers containing sort icons
 */
export const processSortableHeaders = ({ headers, sortConfig, onSort, selectedCategory, dealViewTypes }: ProcessSortableHeadersOptions) => {
    if (!headers?.columns) return headers;

    const processedColumns = headers.columns.map((column: any) => {
        // Check if column has sortConfig enabled
        if (column.sortConfig?.enabled && onSort) {
            // Use sortConfig.field if available, otherwise fall back to column.field
            const sortField = column.sortConfig?.field || column.field;
            const fieldType = column.sortConfig?.fieldType || ''; // Extract fieldType

            // Calculate padding for the header container
            const headerContainerStyle: React.CSSProperties = {
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                width: '100%',
                padding: '0',
                gap: '4px',
                minWidth: 'fit-content', // Ensure there's enough space for icon
            };

            return {
                ...column,
                headerOriginalLabel: column.headerLabel,
                headerLabel: (
                    <div
                        className="header-cell sortable-header"
                        style={headerContainerStyle}
                        onMouseEnter={(e) => {
                            // Show sort icon when hovering over this specific header (including padding area)
                            const sortIconElement = e.currentTarget.querySelector(`.${sortIconStyles.SortIcon}`);
                            if (sortIconElement) {
                                // Check if this sort icon is currently active (has a direction)
                                const isCurrentlyActive = sortConfig?.field === sortField && sortConfig?.direction !== null;

                                if (!isCurrentlyActive) {
                                    // Add hover class to show the icon
                                    sortIconElement.classList.add(sortIconStyles.SortIconHovered);
                                }
                            }
                        }}
                        onMouseLeave={(e) => {
                            // Hide sort icon when leaving this specific header (unless active)
                            const sortIconElement = e.currentTarget.querySelector(`.${sortIconStyles.SortIcon}`);
                            if (sortIconElement) {
                                // Check if this sort icon is currently active (has a direction)
                                const isCurrentlyActive = sortConfig?.field === sortField && sortConfig?.direction !== null;

                                if (!isCurrentlyActive) {
                                    // Remove hover class to hide the icon
                                    sortIconElement.classList.remove(sortIconStyles.SortIconHovered);
                                }
                            }
                        }}
                    >
                        <span style={{ flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {column.headerLabel}
                        </span>
                        {column.headerTooltip && (
                            <span className={sortIconStyles.HeaderTooltipWrapper}>
                                <Tooltip
                                    content={column.headerTooltip}
                                    direction="bottom"
                                    usePortal={true}
                                    fitContent={false}
                                    style={{ maxWidth: '15.563rem', minWidth: 'unset' }}
                                    contentCss={{ color: 'var(--table-heading-text-color)' }}
                                >
                                    <img
                                        src={infoIconGray}
                                        alt="info"
                                        className={sortIconStyles.HeaderTooltipIcon}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </Tooltip>
                            </span>
                        )}
                        <SortIcon
                            field={sortField}
                            sortConfig={sortConfig}
                            onSort={() => onSort(sortField, fieldType)}
                            ascToolTipText={column.sortConfig?.ascToolTipText}
                            descToolTipText={column.sortConfig?.descToolTipText}
                        />
                    </div>
                ),
            };
        }

        return column;
    });

    return {
        ...headers,
        columns: processedColumns,
    };
};
